import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { DomSanitizer } from '@angular/platform-browser';
import { Router, RouterLink } from '@angular/router';
import { IonicModule, Platform } from '@ionic/angular';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { DswAppSwitcherComponent, DswLoginComponent } from '@vi-succ/design-system-web';
import { Catalog, activeCatalogSelector, availableCatalogsSelector } from '@vi-succ/products-domain';
import { ShippingItem, ShoppingCart, ShoppingCartFacade } from '@vi-succ/shoppingcart-domain';
import { Observable, filter, first, map, switchMap, take, withLatestFrom } from 'rxjs';
import { AuthService } from 'src/app/core/login/services/auth.service';
import { LocaleService } from '../../core/locale/services/locale.service';
import { ProtectedTranslateService } from '../../core/locale/services/protected-translate.service';
import { ContextInfo } from '../../core/login/models/login.model';
import { selectUserContextInfo } from '../../core/login/state';
import { environment } from '../../environments/environment';
import { AppSwitcherService } from '../../services/app-switcher.service';
import { BrowserService } from '../../services/browser.service';
import { EtCacheService } from '../../services/et-cache.service';
import { EtCatalogDataService } from '../../services/et-catalog-data.service';
import { EtColoringService } from '../../services/et-coloring.service';
import { LinkFactory } from '../../util';
import {
  CatalogSwitcherDialogComponent,
  CatalogSwitcherDialogData,
} from '../catalog/catalog-switcher-dialog/catalog-switcher-dialog.component';
import { Language, SalesOrg } from '../catalog/catalog-switcher-dialog/catalog-switcher/catalog-switcher.component';
import { FeedbackDialogComponent, FeedbackDialogData } from '../feedback/feedback-dialog.component';
import { LanguageDialogComponent } from '../language-dialog/language-dialog.component';
import { LoginScreenComponent } from '../setup-login/login-screen/login-screen.component';

@Component({
  selector: 'et-et-header',
  templateUrl: './et-header.component.html',
  styleUrls: ['./et-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [
    MatSidenavModule,
    LoginScreenComponent,
    MatDividerModule,
    MatButtonModule,
    MatIconModule,
    IonicModule,
    MatBadgeModule,
    AsyncPipe,
    TranslateModule,
    DswLoginComponent,
    DswAppSwitcherComponent,
    RouterLink,
  ],
})
export class EtHeaderComponent {
  @ViewChild(MatSidenav) sidenav!: MatSidenav;
  isAuthenticated$ = this.authService.authenticated$;
  shoppingCartContent$: Observable<ShoppingCart> = this.shoppingCartFacade.shoppingCartContent$;
  showCart$ = this.authService.canOrder$;
  contextInfoLoading$: Observable<boolean> = this.authService.contextInfoLoading$;
  authenticated$: Observable<boolean> = this.authService.authenticated$;
  userInfo$: Observable<ContextInfo | null> = this.authService.userInfo$;
  appSwitcherContent$ = this.userInfo$.pipe(
    filter((info) => !!info),
    switchMap((info: ContextInfo | null) => this.appSwitcherService.getApps(<string>info?.loginId)),
  );
  accountUrl = environment.backend.accountUrl;
  isDesktop = this.platform.is('desktop');
  appLocales$ = this.localeService.appLocales$;

  constructor(
    iconRegistry: MatIconRegistry,
    sanitizer: DomSanitizer,
    private changeDetectorRef: ChangeDetectorRef,
    private dialog: MatDialog,
    private store: Store,
    private router: Router,
    private authService: AuthService,
    private shoppingCartFacade: ShoppingCartFacade,
    public translate: ProtectedTranslateService,
    private browserService: BrowserService,
    private catalogService: EtCatalogDataService,
    private cacheService: EtCacheService,
    private colorService: EtColoringService,
    public appSwitcherService: AppSwitcherService,
    private platform: Platform,
    public localeService: LocaleService,
  ) {
    this.shoppingCartFacade.loadShoppingCartContent();
    iconRegistry.addSvgIcon('brand', sanitizer.bypassSecurityTrustResourceUrl('assets/icons/brand.svg'));
  }

  onOpenCart() {
    this.router.navigate(['/cart']);
  }

  onLogout() {
    this.authService.logout();
  }

  onOpenOrders() {
    this.openUrlWithLoginContext(`${environment.backend.shopUrl}/orders`, 'Viessmann PartnerShop');
  }

  onLogin() {
    this.authService.login();
  }

  onRegisterClicked() {
    this.authService.register();
  }

  toggleSidenav() {
    if (this.sidenav.opened) {
      this.onCloseSidenav();
    } else {
      this.onOpenSidenav();
    }
  }

  onOpenSidenav() {
    this.colorService.setSecondaryWhiteColor();
    this.sidenav.open();
  }

  onCloseSidenav() {
    this.colorService.revertColor();
    this.sidenav.close();
  }

  onCatalogChange() {
    this.store
      .select(availableCatalogsSelector)
      .pipe(
        filter((catalogs) => catalogs.length > 0),
        first(),
        map((catalogs) => {
          // group catalogs by salesOrg
          return catalogs.reduce(
            (acc, catalog) => {
              return {
                ...acc,
                [catalog.salesOrg]: {
                  value: catalog.salesOrg,
                  languages: [
                    ...(acc[catalog.salesOrg]?.languages ?? []),
                    {
                      value: catalog.language,
                    },
                  ] as Language[],
                } as SalesOrg,
              };
            },
            {} as { [salesOrg: string]: SalesOrg },
          );
        }),
        withLatestFrom(this.store.select(activeCatalogSelector)),
        switchMap(([salesOrgs, catalog]) => {
          return this.dialog
            .open<CatalogSwitcherDialogComponent, CatalogSwitcherDialogData, Catalog | null>(
              CatalogSwitcherDialogComponent,
              {
                ...this.dialogConfig,
                data: {
                  salesOrgs,
                  currentCatalog: catalog,
                  showCancel: true,
                  saveButtonLabel: this.translate.instant('common.save'),
                },
              },
            )
            .afterClosed();
        }),
      )
      .subscribe((catalog) => {
        if (catalog) {
          this.sidenav.close();
          this.changeDetectorRef.detectChanges();
          this.catalogService.setActiveCatalog(catalog).then(() => {
            this.shoppingCartFacade.shoppingCartContent$
              .pipe(
                filter((c) => !!c),
                take(1),
              )
              .subscribe((cart) => {
                if (cart.salesOrg !== catalog.salesOrg) {
                  this.shoppingCartFacade.resetCart();
                } else {
                  //Todo: update language and save
                  // const updatedCart = JSON.parse(JSON.stringify(cart)) as ShoppingCart;
                  //  this.shoppingCartFacade.updateShoppingCartContent(updatedCart);
                }
              });
            this.cacheService.clearCache();
            this.router.navigate(['/']);
          });
        }
      });
  }

  onLanguageChange() {
    this.dialog
      .open<LanguageDialogComponent>(LanguageDialogComponent, this.dialogConfig)
      .afterClosed()
      .subscribe((selectedLanguage) => {
        if (selectedLanguage) this.translate.forceUse(selectedLanguage);
      });
  }

  private get dialogConfig(): MatDialogConfig {
    return {
      width: '100%',
      maxWidth: this.isDesktop ? '722px' : '100vw',
      height: this.isDesktop ? 'unset' : '100%',
      maxHeight: '100vh',
      panelClass: ['menu-dialog-container', ...(this.isDesktop ? ['desktop-scrollbar'] : [])],
    };
  }

  getCartItemsQuantity(items: ShippingItem[]) {
    return items.reduce((sum, current) => sum + current.quantity, 0);
  }

  onManageAccountClicked() {
    this.openUrlWithLoginContext(environment.backend.accountUrl, 'Viessmann Account');
  }

  onOtherViApps() {
    this.store
      .select(activeCatalogSelector)
      .pipe(first())
      .subscribe((catalog) => {
        if (!catalog) {
          return;
        }
        const url = LinkFactory.getOtherAppsLinkForCatalog(catalog);

        this.openUrlWithLoginContext(url, 'Viessmann PartnerPortal');
      });
  }

  onSupportClicked() {
    this.store
      .select(activeCatalogSelector)
      .pipe(withLatestFrom(this.authService.authenticated$), first())
      .subscribe(([catalog, isAuthenticated]) => {
        if (!catalog) {
          return;
        }
        const url = LinkFactory.getSupportLinkForCatalog(catalog, isAuthenticated);

        this.openUrlWithLoginContext(url, 'Viessmann PartnerPortal');
      });
  }

  onFeedbackClicked() {
    let maxWidth = '100vw';
    let maxHeight = '100vh';
    const panelClasses = ['menu-dialog-container'];

    if (this.isDesktop) {
      maxWidth = '722px';
      maxHeight = '90vh';
      panelClasses.push('desktop-scrollbar');
    }

    this.store
      .select(selectUserContextInfo)
      .pipe(
        first(),
        switchMap((contextInfo) =>
          this.dialog
            .open<FeedbackDialogComponent, FeedbackDialogData, void>(FeedbackDialogComponent, {
              data: {
                email: contextInfo?.mail ?? '',
              },
              width: '100%',
              maxWidth: maxWidth,
              height: '100%',
              maxHeight: maxHeight,
              panelClass: panelClasses,
            })
            .afterClosed(),
        ),
      )
      .subscribe();
  }

  private openUrlWithLoginContext(originalUrl: string, title: string) {
    const url = this.authService.hasValidCsrfToken()
      ? `${originalUrl}?sessionid=1#token=${this.authService.getCsrfToken()}`
      : originalUrl;

    this.browserService.open(url, '_blank', title);
  }

  onStartPageClicked() {
    this.router.navigate(['/']);
  }

  goToOverview() {
    // For some reason, when calling this.router.navigate(['/']), the app does not navigate to the overview page.
    // Instead, the tabs are switched to the first tab.
    // Therefore, we call this.router.navigate(['/']) the second time, to really go to the overview page.
    this.router.navigate(['/']).then(() => {
      this.router.navigate(['/']);
    });
  }
}
